import { sbEditable } from '@storyblok/storyblok-editable';
import { render } from 'storyblok-rich-text-react-renderer';
import classNames from 'classnames';
import type { ReactElement, RefObject, Ref } from 'react';
import { useRef, useState } from 'react';
import { TransitionPresence } from 'util/react-transition-component';
import useWindowSize from '../../../hooks/useWindowSize';
import styles from './ModernCreativity.module.scss';
import type { ModernCreativityProps, ModernCreativityRef } from './ModernCreativity.data';
import useSetupTimeline from '../../../hooks/useSetupTimeline';
import useScrollTrigger from '../../../hooks/useScrollTrigger';
import Heading from '../../atoms/heading/Heading';
import Text from '../../atoms/text/Text';
import Image from '../../atoms/image/Image';
import { setupSliderTimeline } from './ModernCreativity.transitions';
// import Video from '../../atoms/video/Video';
import { hasVideo } from '../../../util/storyblok/data.utils';
import { useIsDesktopDevice } from '../../../hooks/useIsDesktopDevice';
import dynamic from 'next/dynamic';

const Video = dynamic(() => import('../../atoms/video/Video'));

export default function ModernCreativity({
  ref,
  ...props
}: ModernCreativityProps & { ref?: Ref<ModernCreativityRef> }): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;
  const keyPrefix = props.blok._uid;
  const contentFrameRef = useRef<HTMLDivElement>(null);
  const carouselWrapperRef = useRef<HTMLDivElement>(null);
  const carouselItemsRef = useRef<Array<HTMLElement>>([]);
  const imagesRef = useRef<Array<HTMLElement>>([]);
  const windowSize = useWindowSize();
  const slideTimeline = useRef<gsap.core.Timeline>(undefined);
  const indexInCenter = useRef<number>(-1);
  const [activeIndex, setActiveIndex] = useState(-1);
  const isDesktopDevice = useIsDesktopDevice();

  slideTimeline.current = useSetupTimeline(setupSliderTimeline, {
    carouselItemsRef,
    imagesRef,
    windowWidth: windowSize.width,
    isInCenterChange: async (index: number, inCenter: boolean) => {
      if (inCenter) {
        setActiveIndex(index);
        indexInCenter.current = index;
      } else if (index === indexInCenter.current && !inCenter) {
        indexInCenter.current = -1;
        setActiveIndex(-1);
      }
    },
  });

  useScrollTrigger({
    trigger: elementRef,
    pin: contentFrameRef,
    vars: {
      scrub: true,
      start: 'top top',
      end: () =>
        `+=${
          (carouselWrapperRef?.current?.offsetWidth || 0) +
          windowSize.width * props.blok.items.length
        }`,
      pinType: 'transform',
      anticipatePin: 1,
    },
  });

  const { inView } = useScrollTrigger({
    trigger: elementRef,
    enableInView: true,
    // endTrigger: contentFrameRef,
    vars: {
      scrub: true,
      start: `top center+=${windowSize.width / 2}`,
      end: () =>
        `+=${
          (carouselWrapperRef?.current?.offsetWidth || 0) +
          windowSize.width * props.blok.items.length +
          windowSize.width +
          windowSize.height
        }`,
      onUpdate: ({ progress }) => {
        slideTimeline?.current?.progress(progress);
      },
    },
  });

  // @ts-ignore
  return (
    <div
      ref={elementRef}
      className={classNames(styles.modernCreativity, props.className)}
      {...sbEditable(props.blok)}
    >
      <div ref={contentFrameRef} className={classNames(styles.contentFrame, 'grid-gutter')}>
        <Heading
          as="h2"
          size="heading-04"
          className={classNames(styles.title)}
          disableTriggerInOnScroll={false}
          transitionInType="splitLineRotateIn"
        >
          {props.blok.title}
        </Heading>

        <Text
          as="span"
          size="kanji-medium"
          className={classNames(styles.kanjiText)}
          disableTriggerInOnScroll={false}
          transitionInType="slideUpFadeIn"
          lang="ja"
        >
          {props.blok.kanjiText}
        </Text>

        <div className={classNames(styles.carouselWrapper)} ref={carouselWrapperRef}>
          <div className={classNames(styles.carouselContainer)}>
            {props?.blok?.items?.map((item, index) => (
              <div
                key={`${item._uid}`}
                {...sbEditable(item)}
                ref={(ref) => {
                  carouselItemsRef.current[index] = ref as HTMLElement;
                }}
                className={classNames(styles.carouselItem)}
              >
                {(!(item.video && hasVideo(item.video)) || !isDesktopDevice) && (
                  <Image
                    ref={(ref) => {
                      imagesRef.current[index] = ref as HTMLElement;
                    }}
                    src={item.image}
                    srcDesktop={item.image}
                    className={classNames(styles.image)}
                    layout="fill"
                    objectFit="cover"
                    alt={item.title}
                  />
                )}

                {item.video && hasVideo(item.video) && isDesktopDevice && (
                  <Video
                    blok={{
                      ...item.video[0],
                      poster_image: {
                        filename: item.image,
                      },
                    }}
                    removeOnPause={!inView}
                    playing={activeIndex === index}
                    requireClickListener
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        <TransitionPresence>
          {activeIndex >= 0 && (
            <Heading
              key={`${keyPrefix}_itemTitle_${activeIndex}`}
              as="h3"
              size="heading-01"
              className={classNames(styles.carouselTitle)}
              transitionOnMount
              transitionOnUnMount
              transitionOutType="splitCharsSlideOutUp"
            >
              {render(props.blok.items[activeIndex].title)}
            </Heading>
          )}
        </TransitionPresence>
        <TransitionPresence>
          {activeIndex >= 0 && (
            <Text
              key={`${keyPrefix}_itemSubTitle_${activeIndex}`}
              as="p"
              size="link-large"
              className={classNames(styles.carouselSubTitle)}
              transitionOnMount
              transitionOnUnMount
            >
              {props.blok.items[activeIndex].subTitle}
            </Text>
          )}
        </TransitionPresence>
      </div>
      <div className={classNames(styles.safariScrollVideoFix, 'abs-fill')} />
    </div>
  );
}
